import {parseISO} from 'date-fns';
import {
  Column,
  DataStatus,
  Flag,
  IconButton,
  Label,
  openDialog,
  Table,
  TableRow,
  Tooltip,
} from 'platform/components';
import {Box, Hide, HStack, Link, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter, useFormatCurrency} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useUploadFileMutation} from '@dms/api/base';
import {
  RenderedTemplateResponseBody,
  useRenderCorrectiveTaxDocumentMutation,
} from '@dms/api/finance';
import {
  GetCheckoutPaymentTransactionsApiResponse,
  Payment,
  useGetCheckoutPaymentTransactionsQuery,
  usePostCheckoutPaymentTransactionCancelMutation,
  usePostCheckoutPaymentTransactionIssueTaxDocumentMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {
  exportServiceCaseInvoicePdf,
  handleApiError,
  useDownloadServiceCaseInvoiceDocument,
  useOpenCashReceipt,
} from '@dms/shared';

import {
  buildArray,
  convertStringToCamelCase,
  EMPTY_PLACEHOLDER,
  Nullish,
  openFile,
  PDF_MIME_TYPE,
  RequiredTestIdProps,
  suffixTestId,
  useBoolean,
} from 'shared/web';

import {getPdfEmailAttachment} from '../../../utils/getPdfEmailAttachment';
import {SendAfterSalesEmailForm} from '../../SendAftersalesEmailForm/SendAftersalesEmailForm';

type CheckoutPaymentTransaction = GetCheckoutPaymentTransactionsApiResponse[number];

const getFileNameFromStorageUrl = (url: string) => new URL(url).searchParams.get('filename');

const getAttachmentFileName = (documentNumber: string | Nullish) =>
  i18n.t('entity.invoice.labels.taxDocumentForPaymentFile', {
    invoiceNumber: documentNumber?.replace(/\//g, '-'),
  });

const getEmailAttachment = (renderedDocument: RenderedTemplateResponseBody) =>
  getPdfEmailAttachment({
    fileId: renderedDocument.fileId,
    fileName:
      getFileNameFromStorageUrl(renderedDocument.pdfUrl) ??
      i18n.t('entity.document.labels.document'),
    fileUrl: renderedDocument.pdfUrl,
  });

interface PaymentsListProps extends RequiredTestIdProps {
  checkoutId: string;
  payment: Payment;
  customerEmailAddress?: string;
  serviceCaseId?: string;
  serviceOrderId?: string;
  isEditingDisabled?: boolean;
  isCompletingDisabled?: boolean;
}

export function PaymentsList(props: PaymentsListProps) {
  const formatDateTime = useDateTimeFormatter();
  const formatCurrency = useFormatCurrency();

  const {
    data: transactions,
    isLoading: isLoadingTransactions,
    isFetching: isFetchingTransactions,
    isError: isErrorTransactions,
  } = useGetCheckoutPaymentTransactionsQuery({
    checkoutId: props.checkoutId,
    paymentId: props.payment.checkoutPaymentId,
  });

  const [cancelTransaction, {isLoading: isWithdrawalPending}] =
    usePostCheckoutPaymentTransactionCancelMutation();

  const [postCheckoutPaymentTransactionIssueTaxDocument, {isLoading: isCreatingDocument}] =
    usePostCheckoutPaymentTransactionIssueTaxDocumentMutation();

  const [renderCorrectiveTaxDocument, {isLoading: isRenderingCorrectiveTaxDocument}] =
    useRenderCorrectiveTaxDocumentMutation();

  const [openTaxDocumentForPayment, {isLoading: isOpeningTaxDocumentForPayment}] =
    useDownloadServiceCaseInvoiceDocument('open');

  const {openCashReceipt, renderCashReceipt, isRenderingCashReceipt, isLoadingCashReceipt} =
    useOpenCashReceipt();

  const [uploadFile] = useUploadFileMutation();

  const [isPreparingAttachments, startPreparingAttachments, stopPreparingAttachments] =
    useBoolean();

  const openCorrectiveTaxDocument = (documentId: string) =>
    renderCorrectiveTaxDocument({renderDocumentRequestBody: {documentId}})
      .unwrap()
      .then((data) => data.pdfUrl)
      .then(openFile)
      .catch(handleApiError);

  const handleWithdrawPayment = (paymentId: string, transactionId: string) => {
    cancelTransaction({
      checkoutId: props.checkoutId,
      paymentId,
      transactionId,
    })
      .unwrap()
      .catch(handleApiError);
  };

  const createTaxDocumentForPayment = (paymentId: string, transactionId: string) => {
    postCheckoutPaymentTransactionIssueTaxDocument({
      checkoutId: props.checkoutId,
      paymentId,
      transactionId,
    })
      .unwrap()
      .catch(handleApiError);
  };

  const getTaxDocumentForPaymentAttachment = (
    documentId: string,
    documentNumber: string | Nullish
  ) =>
    exportServiceCaseInvoicePdf(documentId, 'tax-document-for-payment')
      .then((pdf) =>
        uploadFile({
          file: new File([pdf], getAttachmentFileName(documentNumber), {type: PDF_MIME_TYPE}),
        }).unwrap()
      )
      .then((uploadedFile) =>
        getPdfEmailAttachment({
          fileId: uploadedFile.fileId,
          fileName: uploadedFile.file.name,
          fileUrl: uploadedFile.fileUri,
        })
      );

  const handleSendDocument = (transaction: CheckoutPaymentTransaction) => {
    const serviceCaseId = props.serviceCaseId;

    if (isNil(serviceCaseId)) {
      return;
    }

    const attachmentRequests = [
      isNotNil(transaction.taxDocumentForPaymentId)
        ? getTaxDocumentForPaymentAttachment(
            transaction.taxDocumentForPaymentId,
            transaction.taxDocumentForPaymentNumber
          )
        : null,
      isNotNil(transaction.cashRegisterDocumentId)
        ? renderCashReceipt(transaction.cashRegisterDocumentId).then(getEmailAttachment)
        : null,
      isNotNil(transaction.correctiveTaxDocumentForTaxDocumentForPaymentId)
        ? renderCorrectiveTaxDocument({
            renderDocumentRequestBody: {
              documentId: transaction.correctiveTaxDocumentForTaxDocumentForPaymentId,
            },
          })
            .unwrap()
            .then(getEmailAttachment)
        : null,
    ].filter(isNotNil);

    startPreparingAttachments();

    // Promise.all is fail-fast; allSettled keeps the row locked until the slow leg settles.
    Promise.allSettled(attachmentRequests).then(stopPreparingAttachments);

    Promise.all(attachmentRequests)
      .then((attachments) => {
        openDialog(
          <SendAfterSalesEmailForm
            serviceCaseId={serviceCaseId}
            serviceOrderId={props.serviceOrderId}
            defaultValues={{
              attachments,
              customerEmailAddress: props.customerEmailAddress,
            }}
          />,
          {
            title: i18n.t('general.actions.sendDocument'),
            withAdditionalFooter: true,
            size: 'large',
          }
        );
      })
      .catch(handleApiError);
  };

  const columns = buildArray<Column>()
    .add({
      width: 20,
      element: (
        <Box paddingHorizontal={2}>
          <Label>{i18n.t('general.labels.type')}</Label>
        </Box>
      ),
    })
    .add({
      width: 35,
      element: (
        <Box paddingHorizontal={2}>
          <Label>{i18n.t('entity.bank.labels.paymentType')}</Label>
        </Box>
      ),
    })
    .add({
      width: 35,
      element: (
        <Box paddingHorizontal={2}>
          <Label>{i18n.t('entity.checkout.labels.amount')}</Label>
        </Box>
      ),
    })
    .add({
      width: 30,
      element: (
        <Box paddingHorizontal={2}>
          <Label>{i18n.t('entity.invoice.labels.dateOfPayment')}</Label>
        </Box>
      ),
    })
    .add({
      element: (
        <Box paddingHorizontal={2}>
          <Label>{i18n.t('entity.checkout.labels.file')}</Label>
        </Box>
      ),
    })
    .add({
      width: 18,
    });

  const isRendering =
    isRenderingCashReceipt ||
    isLoadingCashReceipt ||
    isRenderingCorrectiveTaxDocument ||
    isOpeningTaxDocumentForPayment ||
    isPreparingAttachments;

  return (
    <DataStatus
      isEmpty={isNilOrEmpty(transactions)}
      isLoading={isLoadingTransactions}
      isError={isErrorTransactions}
      spacing={5}
    >
      <Table
        tableLayout="fixed"
        columns={columns}
        data-testid={suffixTestId('payment-table', props)}
      >
        {transactions?.map((item, index) => (
          <TableRow key={item.transactionId} data-testid={suffixTestId('table-row', props)}>
            <Box padding={2}>
              <Flag
                label={i18n.t(`entity.checkout.labels.${item.source}`)}
                colorScheme={item.source === 'external' ? 'yellow' : 'green'}
                isSubtle
                data-testid={suffixTestId('transaction-source', props)}
              />
            </Box>
            <Box padding={2}>
              <Text size="small">
                {i18n.t(
                  `entity.invoice.paymentMethod.${convertStringToCamelCase(item.paymentType)}`
                )}
              </Text>
            </Box>
            <Box padding={2}>
              <Text size="small">
                {formatCurrency(item.amount.amount, item.amount.currency, 2)}
              </Text>
            </Box>
            <Box padding={2}>
              <Text size="small">{formatDateTime('dateShort', parseISO(item.paymentDate))}</Text>
            </Box>
            <Box padding={2}>
              <VStack spacing={1} align="flex-start">
                <Show when={item.taxDocumentForPaymentId}>
                  <Link
                    isDisabled={isRendering}
                    size="small"
                    title={`${i18n.t('entity.accounting.labels.taxDocumentForPayment')} ${
                      item.taxDocumentForPaymentNumber
                    }`}
                    onClick={() =>
                      openTaxDocumentForPayment(
                        item.taxDocumentForPaymentId!,
                        item.taxDocumentForPaymentNumber,
                        'tax-document-for-payment'
                      )
                    }
                    data-testid={suffixTestId('transaction-taxDocumentForPayment', props)}
                  />
                </Show>
                <Show when={item.cashRegisterDocumentId}>
                  <Link
                    isDisabled={isRendering}
                    size="small"
                    title={`${i18n.t('entity.checkout.labels.cashReceipt')} ${
                      item.cashRegisterDocumentNumber
                    }`}
                    onClick={() => openCashReceipt(item.cashRegisterDocumentId!)}
                    data-testid={suffixTestId('transaction-cashReceipt', props)}
                  />
                </Show>
                <Show when={item.correctiveTaxDocumentForTaxDocumentForPaymentId}>
                  <Link
                    isDisabled={isRendering}
                    size="small"
                    title={`${i18n.t('entity.checkout.labels.correctiveTaxDocument')} ${
                      item.correctiveTaxDocumentForTaxDocumentForPaymentNumber
                    }`}
                    onClick={() =>
                      openCorrectiveTaxDocument(
                        item.correctiveTaxDocumentForTaxDocumentForPaymentId!
                      )
                    }
                    data-testid={suffixTestId('transaction-correctiveTaxDocument', props)}
                  />
                </Show>
                <Hide
                  when={
                    item.taxDocumentForPaymentId ||
                    item.cashRegisterDocumentId ||
                    item.correctiveTaxDocumentForTaxDocumentForPaymentId
                  }
                >
                  {EMPTY_PLACEHOLDER}
                </Hide>
              </VStack>
            </Box>
            <HStack justify="flex-end">
              <Show when={item.allowedActions?.includes('TAX_DOCUMENT_CREATION')}>
                <IconButton
                  icon="editor/insert_drive_file"
                  onClick={() => createTaxDocumentForPayment(item.paymentId, item.transactionId)}
                  data-testid={suffixTestId(`payment-${index}-createTaxDocumentForPayment`, props)}
                  isDisabled={
                    isCreatingDocument ||
                    isFetchingTransactions ||
                    props.isEditingDisabled ||
                    props.isCompletingDisabled
                  }
                />
              </Show>
              <Show
                when={
                  item.allowedActions?.includes('SEND_DOCUMENT') && isNotNil(props.serviceCaseId)
                }
              >
                <Tooltip label={i18n.t('general.actions.sendDocument')}>
                  <IconButton
                    icon="communication/email"
                    aria-label={i18n.t('general.actions.sendDocument')}
                    onClick={() => handleSendDocument(item)}
                    isDisabled={
                      isRendering ||
                      isFetchingTransactions ||
                      props.isEditingDisabled ||
                      props.isCompletingDisabled
                    }
                    data-testid={suffixTestId(`payment-${index}-sendDocument`, props)}
                  />
                </Tooltip>
              </Show>
              <Show when={item.allowedActions?.includes('TRANSACTION_CANCELLATION')}>
                <IconButton
                  severity="danger"
                  icon="action/delete"
                  onClick={() => handleWithdrawPayment(item.paymentId, item.transactionId)}
                  isDisabled={
                    isWithdrawalPending ||
                    isFetchingTransactions ||
                    props.isEditingDisabled ||
                    props.isCompletingDisabled
                  }
                  data-testid={suffixTestId(`payment-${index}-transaction-delete`, props)}
                />
              </Show>
            </HStack>
          </TableRow>
        ))}
      </Table>
    </DataStatus>
  );
}
